import { useParams, useNavigate } from 'react-router'
import { ArrowLeft, Copy, Check } from 'lucide-react'
import { useState } from 'react'
import { useThreadMessages } from '@/hooks/useApi'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { LoadingSkeleton, TableSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { EmptyState } from '@/components/ui/empty-state'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { formatISODateTime } from '@/lib/format-utils'

export function ThreadDetailsPage() {
  const { threadId } = useParams<{ threadId: string }>()
  const navigate = useNavigate()
  const { data, isLoading, error, refetch } = useThreadMessages(threadId)
  const { toast } = useToast()
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const handleCopy = async (messageId: string) => {
    try {
      await navigator.clipboard.writeText(messageId)
      setCopiedId(messageId)
      setTimeout(() => setCopiedId(null), 3000)
    } catch {
      toast({
        title: 'Failed to copy',
        description: 'Could not copy to clipboard',
        variant: 'destructive',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <LoadingSkeleton className="h-10 w-10" />
          <LoadingSkeleton className="h-8 w-64" />
        </div>
        <TableSkeleton rows={5} cols={4} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold">Thread {threadId}</h1>
        </div>
        <ErrorState error={error} onRetry={refetch} />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Thread {threadId}</h1>
          <p className="text-sm text-muted-foreground">
            {data?.messages?.length ?? 0} message
            {data?.messages?.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {!data?.messages || data.messages.length === 0 ? (
        <EmptyState message="No messages found for this thread" />
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sent At</TableHead>
                <TableHead>From</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Message ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.messages.map(message => (
                <TableRow key={message.message_id}>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatISODateTime(message.sent_at ?? '')}
                  </TableCell>
                  <TableCell>{message.from || '—'}</TableCell>
                  <TableCell>{message.subject || '—'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{message.message_id}</span>
                      <Tooltip open={copiedId === message.message_id}>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleCopy(message.message_id)}
                          >
                            {copiedId === message.message_id ? (
                              <Check className="h-4 w-4 text-mint" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Copied!</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
